import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar, faReceipt } from "@fortawesome/free-solid-svg-icons"
import i18next from "i18next"
import type { ListItem } from "../../components/List/List"
import { convertToLocalMoney } from "../Generics/Helps"
import { ManualExternalOrder } from "../ManualExternalOrder/ManualExternalOrder"
import {
  getManualExternalOrderDeliveryLabel,
  getManualExternalOrderPaymentLabel,
  getManualExternalOrderReconciliationClassName,
  getManualExternalOrderReconciliationLabel,
  getManualExternalOrderSourceLabel,
  getManualExternalOrderStatusClassName,
  getManualExternalOrderStatusLabel,
} from "../ManualExternalOrder/manualExternalOrderHelpers"
import { MemoryClass } from "./MemoryClass"

export class ManualExternalOrderMemory extends MemoryClass<ManualExternalOrder> {
  setList(items: ManualExternalOrder[]): void {
    this.listData = items
  }

  getListCards(all: boolean): ListItem[] {
    const cards: ListItem[] = []
    let count = 0

    this.listData.forEach((item) => {
      if (all) {
        cards.push(this.getCard(item, count))
        count++
      }
    })

    return cards
  }

  getCard(order: ManualExternalOrder, count: number): ListItem {
    return {
      id: order.id,
      title: order.customerName || order.externalOrderCode || getManualExternalOrderSourceLabel(order.sourceType),
      linkImage: "",
      index: count,
      dataFilter: [
        order.customerName ?? "",
        order.customerPhone ?? "",
        order.externalOrderCode ?? "",
        order.internalNotes ?? "",
        getManualExternalOrderSourceLabel(order.sourceType),
      ],
      children: (
        <div className="grid gap-3 rounded-xl border border-orange-100 bg-white p-4 transition-all duration-300 hover:scale-[1.01] hover:shadow-lg">
          <div className="grid gap-2">
            <h3 className="break-words text-base font-extrabold text-slate-900">
              {order.customerName || i18next.t("manualExternalOrder:list.no_customer")}
            </h3>

            <div className="grid gap-2 text-xs text-slate-600">
              <span className="w-fit rounded-full bg-orange-100 px-2 py-1 font-semibold text-orange-700">
                {getManualExternalOrderSourceLabel(order.sourceType)}
             
                {order.sourceName ? ` - ${order.sourceName}` : ""}
              </span>

              <span className="w-fit rounded-full bg-slate-100 px-2 py-1 font-semibold">
                <FontAwesomeIcon icon={faCalendar} /> {order.orderDate.toReadableDate()}
              </span>

              {order.externalOrderCode && (
                <span className="w-fit rounded-full bg-sky-100 px-2 py-1 font-semibold text-sky-700">
                  {order.externalOrderCode}
                </span>
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <span
              className={`w-fit rounded-full px-2 py-1 text-xs font-bold ${getManualExternalOrderStatusClassName(order.status)}`}
            >
              {getManualExternalOrderStatusLabel(order.status)}
            </span>

            <span
              className={`w-fit rounded-full px-2 py-1 text-xs font-bold ${getManualExternalOrderReconciliationClassName(order.reconciliationStatus)}`}
            >
              {getManualExternalOrderReconciliationLabel(order.reconciliationStatus)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="rounded-xl bg-slate-50 p-2">
              <p className="text-xs font-semibold text-slate-500">
                {i18next.t("manualExternalOrder:fields.payment_method")}
              </p>
              <strong>{getManualExternalOrderPaymentLabel(order.paymentMethod)}</strong>
            </div>

            <div className="rounded-xl bg-slate-50 p-2">
              <p className="text-xs font-semibold text-slate-500">
                {i18next.t("manualExternalOrder:fields.delivery_type")}
              </p>
              
              <strong>{getManualExternalOrderDeliveryLabel(order.deliveryType)}</strong>
            </div>

            <div className="rounded-xl bg-orange-50 p-2">
              <p className="text-xs font-semibold text-orange-700">
                {i18next.t("manualExternalOrder:fields.gross_amount")}
              </p>
              
              <strong>{convertToLocalMoney(order.grossAmount)}</strong>
            </div>

            <div className="rounded-xl bg-red-50 p-2">
              <p className="text-xs font-semibold text-red-700">
                {i18next.t("manualExternalOrder:list.fees_discounts")}
              </p>
              
              <strong>
                {convertToLocalMoney(
                  order.platformFeeAmount + order.paymentFeeAmount + order.otherCostsAmount + order.discountAmount,
                )}
              </strong>
            </div>

            <div className="rounded-xl bg-emerald-50 p-2">
              <p className="text-xs font-semibold text-emerald-700">
                {i18next.t("manualExternalOrder:fields.net_amount")}
              </p>

              <strong>{convertToLocalMoney(order.netAmount)}</strong>
            </div>
          </div>

          <span className="border-t border-orange-100 pt-3 text-xs font-semibold text-slate-500">
            <FontAwesomeIcon icon={faReceipt} /> {i18next.t("manualExternalOrder:list.open_details")}
          </span>
        </div>
      ),
    }
  }

  protected getItemId(item: ManualExternalOrder): number {
    return item.id
  }

  protected cloneEntity(item: ManualExternalOrder): ManualExternalOrder {
    return { ...item, items: item.items.map((orderItem) => ({ ...orderItem })) }
  }
}
